import { Dispatch, SetStateAction, FC, useState } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import ComponentError from '@eneco/components/ui/ComponentError/ComponentError';
import { postEnecoBeXapiSiteApiV1AccountsByAccountNumberForgotEmail } from '@eneco/dc-be/client';
import unwrapData from '@eneco/dc-be/utils/unwrapData';
import { unwrapNotFoundError } from '@eneco/dc-be/utils/unwrapError';
import { typedYupResolver } from '@eneco/hooks/form';
import {
  Box,
  Button,
  ButtonLink,
  Card,
  Dialog,
  Form,
  Grid,
  Heading,
  IconButton,
  Image,
  NotificationBox,
  NumberedList,
  PageGrid,
  Skeleton,
  Stack,
  Text,
  TextLink,
} from '@eneco/sparky';
import { ErrorIcon } from '@eneco/sparky/icons';
import { Fields, ForgotEmailRendering } from '@sitecore/types/ForgotEmail';
import { signIn } from 'next-auth/react';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { InputDigits } from '../../../components/InputDigits';

const NO_DELIVERABLE_EMAIL_ERROR = 'ForgotEmail.Account.NoDeliverableEmail';

interface FormFields {
  customerNumber: string;
}

type CurrentState = 'Initial' | 'Submitted' | 'NoAccount' | 'NoDeliverableEmail';

async function executeApiCall(accountNumber: string) {
  const requestOption = { path: { accountNumber: accountNumber } };
  return await postEnecoBeXapiSiteApiV1AccountsByAccountNumberForgotEmail(requestOption);
}
const InitialScreen = ({
  fields,
  isLoading,
  setLoading,
  setCurrentStep,
  setObfuscatedMail,
  setApiError,
  setCustomerNumber,
}: {
  fields: Fields;
  isLoading: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setCurrentStep: Dispatch<SetStateAction<CurrentState>>;
  setObfuscatedMail: Dispatch<SetStateAction<string | null | undefined>>;
  setApiError: Dispatch<SetStateAction<string | null | undefined>>;
  setCustomerNumber: Dispatch<SetStateAction<string | null | undefined>>;
}) => {
  const [modalOpen, setModalOpen] = useState(false);

  const formSchema = yup.object({
    customerNumber: yup
      .string()
      .min(3, fields.initialScreen.initialScreenCustomerNotFoundValidationNotification?.value.title)
      .required(),
  });

  const formMethods = useForm<FormFields>({
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  const {
    formState: { errors },
    handleSubmit,
    register,
  } = formMethods;

  const onSubmit = async (value: yup.InferType<typeof formSchema>) => {
    try {
      setLoading(true);
      setCustomerNumber(value.customerNumber);
      const result = await executeApiCall(value.customerNumber);
      const data = unwrapData(result);
      if (result.response.ok) {
        setObfuscatedMail(data?.email);
        setCurrentStep('Submitted');
      } else if (unwrapNotFoundError(result.error).includes(NO_DELIVERABLE_EMAIL_ERROR)) {
        setCurrentStep('NoDeliverableEmail');
      } else if (result.response.status === 404) {
        setCurrentStep('NoAccount');
      } else {
        throw new Error();
      }
    } catch {
      setApiError('');
    } finally {
      setLoading(false);
    }
  };

  const TooltipModal = () => (
    <Dialog
      setOpen={() => setModalOpen(true)}
      onClose={() => setModalOpen(false)}
      isOpen={modalOpen}
      trigger={
        <IconButton
          label={fields.initialScreen.initialScreenWhereToFindCustomerNumberTooltipText.value}
          onClick={() => setModalOpen(true)}>
          <ErrorIcon color="iconPrimary" size="medium" />
        </IconButton>
      }
      width="regular">
      <Box>
        <Stack direction="column" gap="6">
          <Image
            src={fields.initialScreen.initialScreenWhereToFindCustomerNumberImage.value.src}
            alt={fields.initialScreen.initialScreenWhereToFindCustomerNumberImage?.value?.alt}
            height={fields.initialScreen.initialScreenWhereToFindCustomerNumberImage?.value?.height ?? '300px'}
            width={fields.initialScreen.initialScreenWhereToFindCustomerNumberImage?.value?.width ?? '100%'}
            objectFit="cover"
          />
          <Heading size="S" as="h2">
            {fields.initialScreenModal.initialScreenModalTitle.value}
          </Heading>
          <RichText html={fields.initialScreenModal.initialScreenModalExplanationContent.value} />
        </Stack>
      </Box>
    </Dialog>
  );

  return (
    <Box>
      <Card as="div" corners="rounded" elevation="S">
        <Card.Content>
          <FormProvider {...formMethods}>
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Stack direction="column" gap="6">
                <Heading size="S" as="h2">
                  {fields.initialScreen.initialScreenTitle.value}
                </Heading>
                <RichText html={fields.initialScreen.initialScreenIntroExplanationContent.value} />
                <Stack direction="row" alignY="center" alignX="start">
                  <TextLink onClick={() => setModalOpen(true)}>
                    {fields.initialScreen.initialScreenWhereToFindCustomerNumberTooltipText.value}
                  </TextLink>
                  <TooltipModal />
                </Stack>
                <InputDigits
                  key={fields.initialScreen.initialScreenCustomerNumberLabel.value}
                  label={fields.initialScreen.initialScreenCustomerNumberLabel.value}
                  defaultValue={''}
                  error={errors?.customerNumber?.message}
                  {...register('customerNumber')}
                />
                <RichText html={fields.initialScreen.initialScreenEmailExplanationContent.value} />
                <Button action="primary" size="regular" tone="onLight" type="submit" isLoading={isLoading}>
                  {fields.initialScreen.initialScreenSendButtonLink.value.title}
                </Button>
              </Stack>
            </Form>
          </FormProvider>
        </Card.Content>
      </Card>
    </Box>
  );
};

const NoAccountScreen = ({ fields, isLoading }: { fields: Fields; isLoading: boolean }) => {
  return (
    <Box>
      <Card as="div" corners="rounded" elevation="S">
        <Card.Content>
          <Stack direction="column" gap="6">
            <Heading size="S" as="h2">
              {fields.noAccountScreen.noAccountScreenTitle.value}
            </Heading>
            <Heading size="XS" as="h3">
              {fields.noAccountScreen.noAccountScreenSubtitle.value}
            </Heading>
            <RichText html={fields.noAccountScreen.noAccountScreenContent.value} />
            <Button
              action="primary"
              size="regular"
              tone="onLight"
              type="submit"
              onClick={async () => await signIn('okta')}
              isLoading={isLoading}>
              {fields.noAccountScreen.noAccountScreenButtonText.value}
            </Button>
          </Stack>
        </Card.Content>
      </Card>
    </Box>
  );
};

const NoDeliverableEmailScreen = ({ fields }: { fields: Fields }) => {
  return (
    <Box>
      <Card as="div" corners="rounded" elevation="S">
        <Card.Content>
          <Stack direction="column" gap="6">
            <Heading size="S" as="h2">
              {fields.noDeliverableEmailScreen.title.value ?? ''}
            </Heading>
            <Heading size="XS" as="h3">
              {fields.noDeliverableEmailScreen.subTitle.value ?? ''}
            </Heading>
            <RichText html={fields.noDeliverableEmailScreen.content.value ?? ''} />
            {fields.noDeliverableEmailScreen.buttonLink.value && (
              <ButtonLink
                action="primary"
                size="regular"
                tone="onLight"
                href={fields.noDeliverableEmailScreen.buttonLink.value?.href ?? ''}>
                {fields.noDeliverableEmailScreen.buttonLink.value?.text ?? ''}
              </ButtonLink>
            )}
          </Stack>
        </Card.Content>
      </Card>
    </Box>
  );
};

const SubmittedScreen = ({
  fields,
  obfuscatedMail,
  customerNumber,
}: {
  fields: Fields;
  customerNumber: string;
  obfuscatedMail: string;
}) => {
  const [lastResend, setLastResend] = useState<Date | null | undefined>(null);
  const [showResendBlockedNotification, setShowResendBlockedNotification] = useState(false);
  const resend = async () => {
    const now = new Date();
    // Check if lastResend is null or more than 1 minute ago
    if (!lastResend || now.getTime() - lastResend.getTime() >= 60000) {
      try {
        await executeApiCall(customerNumber);
      } finally {
        setLastResend(now);
        setShowResendBlockedNotification(false);
      }
    } else {
      setShowResendBlockedNotification(true);
    }
  };

  return (
    <Box>
      <Card as="div" corners="rounded" elevation="S">
        <Card.Content>
          <Stack direction="column" gap="6">
            <Heading size="S" as="h2">
              {fields.confirmationScreen.confirmationScreenTitle.value}
            </Heading>
            {!showResendBlockedNotification && (
              <NotificationBox
                isAlert={false}
                title={fields.notifications.notificationsSendTitle.value}
                text={
                  <>
                    <RichText html={fields.notifications.notificationsSendContent.value} />
                    <span> </span>
                    <Text weight="bold">{obfuscatedMail}</Text>
                  </>
                }
              />
            )}
            {showResendBlockedNotification && (
              <NotificationBox
                isAlert={false}
                variant={'warning'}
                title={fields.notifications.notificationsResendTitle.value}
                text={<RichText html={fields.notifications.notificationsResendContent.value} />}
              />
            )}
            <Heading size="XS" as="h3">
              {fields.confirmationScreen.confirmationScreenSubtitle.value}
            </Heading>
            <NumberedList>
              <Stack direction="column" gap="6">
                <NumberedList.Item>
                  <Text weight="bold">{fields.confirmationScreen.confirmationScreenBulletOneTitle.value}</Text>
                  <RichText html={fields.confirmationScreen.confirmationScreenBulletOneContent.value} />
                  <TextLink emphasis="low" onClick={resend}>
                    {fields.confirmationScreen.confirmationScreenBulletOneActionLink.value.text}
                  </TextLink>
                </NumberedList.Item>
                <NumberedList.Item>
                  <Text weight="bold">{fields.confirmationScreen.confirmationScreenBulletTwoTitle.value}</Text>
                  <RichText html={fields.confirmationScreen.confirmationScreenBulletTwoContent.value} />
                </NumberedList.Item>
              </Stack>
            </NumberedList>
            <RichText html={fields.confirmationScreen.confirmationScreenFootNoteContent.value}></RichText>
          </Stack>
        </Card.Content>
      </Card>
    </Box>
  );
};
const ForgotEmail: FC<ForgotEmailRendering> = ({ fields }) => {
  const [isLoading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null | undefined>(null);
  const [currentStep, setCurrentStep] = useState<CurrentState>('Initial');
  const [obfuscatedMail, setObfuscatedMail] = useState<string | null | undefined>();
  const [customerNumber, setCustomerNumber] = useState<string | null | undefined>();

  return (
    <Box paddingY="10">
      <PageGrid>
        <Grid.Item gridColumn={{ initial: '1/-1', md: '4/-2', lg: '5/9' }} gridRowStart="1">
          {isLoading && (
            <Stack direction="column" gap="6">
              <Skeleton height={400} width={600} />
            </Stack>
          )}
          {!isLoading && !apiError && currentStep === 'Initial' && (
            <InitialScreen
              isLoading={isLoading}
              setLoading={setLoading}
              fields={fields}
              setObfuscatedMail={setObfuscatedMail}
              setApiError={setApiError}
              setCurrentStep={setCurrentStep}
              setCustomerNumber={setCustomerNumber}
            />
          )}
          {!isLoading && !apiError && currentStep === 'Submitted' && (
            <SubmittedScreen fields={fields} customerNumber={customerNumber!} obfuscatedMail={obfuscatedMail!} />
          )}
          {!isLoading && !apiError && currentStep === 'NoAccount' && (
            <NoAccountScreen fields={fields} isLoading={isLoading} />
          )}
          {!isLoading && !apiError && currentStep === 'NoDeliverableEmail' && (
            <NoDeliverableEmailScreen fields={fields} />
          )}
          {apiError && <ComponentError />}
        </Grid.Item>
      </PageGrid>
    </Box>
  );
};

export default ForgotEmail;
